import { API_BASE_URL } from '../../constants/domain';
import ApiInstance from '../../interfaces/api-instance';
import { getSessionTokenFromUrl } from '../../utilis/session-token';
import { POSE_ROUTES } from './route';

/** The backend's `{error}` (or axios's message) on an Error, with the HTTP status when there was one. */
function normalizePoseError(error) {
  const status = error?.response?.status ?? null;
  const body = error?.response?.data ?? null;
  const normalized = new Error(
    (typeof body?.error === 'string' && body.error) || error?.message || 'Pose room request failed'
  );
  normalized.name = 'PoseApiError';
  normalized.status = status;
  normalized.cause = error;
  return normalized;
}

/**
 * The session's LiveKit room, `{ url, token, room, record }`; the backend has already sent the pose
 * service into it. `record`: the backend allowed `recordVideo`, so the pod records its exercises.
 * 401 is a bad session token, 503 LiveKit unconfigured or the service down.
 */
export async function requestLivekitRoom(sessionId, { recordVideo = false } = {}) {
  try {
    const { data } = await ApiInstance.post(
      POSE_ROUTES.LIVEKIT,
      { sessionId: sessionId || null, sessionToken: getSessionTokenFromUrl(), recordVideo },
      {
        headers: { 'Content-Type': 'application/json' },
        // A 401 is the session token, not a login: the pose rung fails, the patient stays here.
        skipAuthRedirect: true,
      }
    );
    if (!data?.url || !data?.token) {
      throw new Error('No LiveKit url or token in the pose room response');
    }
    return {
      url: data.url,
      token: data.token,
      room: data.room ?? '',
      record: data.record === true,
    };
  } catch (error) {
    throw normalizePoseError(error);
  }
}

/**
 * One session's latency summary (workers/pose-latency-probe.js), logged by the backend. `keepalive`, not
 * axios: it is also sent from `pagehide`, and only a keepalive request outlives the page.
 */
export async function postLatencySummary(sessionId, sessionToken, summary) {
  const response = await fetch(
    `${String(API_BASE_URL || '').replace(/\/$/, '')}${POSE_ROUTES.LATENCY}`,
    {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, sessionToken, summary }),
    }
  );
  if (!response.ok) {
    throw new Error(`latency summary refused (${response.status})`);
  }
}
